import React from 'react';
import type { ViewerContext } from '../../types/profileViewerContext';
import { CvImportUploadZone } from '../CandidatePools/CvImportUploadZone';
import type { UsePersonCvUploadResult } from '../../hooks/usePersonCvUpload';
import { showCvUploadInProfileModal, showProfileUpdateActions } from '../../utils/profileCvUpload';

export type { UsePersonCvUploadResult };

interface Props {
  viewerContext: ViewerContext;
  cvUpload: UsePersonCvUploadResult;
}

/** CV upload tile on the Overview tab (key facts are edited from the Key facts panel). */
export const ProfileOverviewUpdateActions: React.FC<Props> = ({ viewerContext, cvUpload }) => {
  if (!showProfileUpdateActions(viewerContext)) return null;

  const showCvTile = showCvUploadInProfileModal(viewerContext);
  const { poolLoadError, canUploadCv, busy } = cvUpload;

  if (!showCvTile) return null;

  return (
    <section className="mb-6" data-testid="profile-overview-update-actions">
      <p className="text-xs font-bold uppercase tracking-wide text-amber-400 mb-3">Update profile</p>

      <div className="max-w-md">
        <CvImportUploadZone cvUpload={cvUpload} idleLabel="Upload CV" />
      </div>

      {poolLoadError && (
        <p className="mt-2 text-xs text-amber-400" data-testid="profile-cv-pool-load-error">
          {poolLoadError}
        </p>
      )}

      {!canUploadCv && !poolLoadError && !busy && (
        <p className="mt-2 text-xs text-dark-text-secondary" data-testid="profile-cv-upload-unavailable">
          Add this person to a talent pool you can edit to upload a CV here, or use a profile-update
          link.
        </p>
      )}
    </section>
  );
};

/** Header row: Upload CV only (key facts edit lives in the Key facts panel). */
export const ProfileHeaderUpdateActions: React.FC<{
  viewerContext: ViewerContext;
  cvUpload: UsePersonCvUploadResult;
}> = ({ viewerContext, cvUpload }) => {
  if (!showProfileUpdateActions(viewerContext)) return null;

  const showCv = showCvUploadInProfileModal(viewerContext);
  const { openFilePicker, busy, canUploadCv } = cvUpload;

  if (!showCv) return null;

  return (
    <div
      className="flex flex-wrap items-center justify-end gap-2 mb-2"
      data-testid="profile-header-update-actions"
    >
      <button
        type="button"
        onClick={openFilePicker}
        disabled={busy || !canUploadCv}
        className="px-4 py-2 rounded-lg text-sm font-bold border border-dark-border text-dark-text hover:bg-dark-bg disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        data-testid="candidate-profile-upload-cv"
      >
        Upload CV
      </button>
    </div>
  );
};
